import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, IconButton, Button, Typography, ThemeProvider,Paper, Stack, Tooltip, Divider, Select, MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import { Link } from 'react-router-dom';
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import TranslatableDataGridServerLoaded from '../shared/TranslatableDataGridServerLoaded';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import DateDisplay from '../DateDisplay';
import CheckBox from '../form-controls/CheckBox';
import OrderAckIcon from '../icons/OrderAckIcon';
import Api from '../../services/Api';
import Notifications from '../../services/Notifications';
import StepProgressBar from '../shared/StepProgressBar';
import MUIModal from '../shared/MUIModal';
import { Container, Row, Col } from 'react-bootstrap';
import CommentDetailsModal from '../claims/CommentDetailsModal';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';

const Claims = ({
    claims = [],
    count = 0,
    fetching = false,
    paginationModel,
    setPaginationModel,
    listView = false,
    localized = {},
    roles = [],
    statusCodes = [],
    claimsStatus = [],
    setClaimApproval,
}) => {
    const gridLocalized = useStorefrontAdministrationConfig().labels;
    const wrapperTheme = StorefrontAdminTheme;
    const rowKey = (c) => `${c.caseNumber ?? 'noCase'}-${c.lineNumber ?? 'noLine'}`;
    const rows = React.useMemo(() => {
        const map = new Map();
        (claims ?? []).forEach((c) => {
            const id = rowKey(c);
            map.set(id, {
                id,
                caseLine: `${c?.caseNumber ?? ''}${c?.lineNumber ? `-${c.lineNumber}` : ''}`,
                ...c,
            });
        });
        return Array.from(map.values());
    }, [claims]);

    const validRMAStatusCode = (statusCode) => {
        if (statusCode === '1PM') return true;
        if (!isNaN(statusCode)) {
            const code = parseInt(statusCode, 10);
            return (code > 129 && code <= 900) || code === 999;
        }
        return false;
    };

    const handleFileError = (res) => {
        if (!res) Notifications.error(localized.NoRMAMessage);
    };

    const getRMA = (claim) => {
        Api.downloadFileName(
            `/api/claims/GetRMA/${claim.billToCustomer}/${claim.rmA_PONumber}/${claim.documentType}`,
            `${claim.billToCustomer}-${claim.rmA_PONumber}${claim.documentType}`
        ).then(handleFileError);
    };

    const getCommentTextFile = (claim) => {
        Api.downloadFileName(
            `/api/claims/getCommentTextFile/${claim.billToCustomer}/${claim.serialNumber}/${claim.documentType}`,
            `${claim.billToCustomer}-${claim.serialNumber}-WarrantyTextFile`
        ).then(handleFileError);
    };

    const getStatusSteps = (claim) => {
        const list = (claimsStatus ?? []).filter(
            (s) => s.returnPolicy === claim.returnPolicy && s.shown === true
        );
        const base = list;
        let byType = base?.filter((s) => s.type === claim.type) ?? [];
        if (claim.type === 'Analysis Only' && claim.providerGroup != null) {
            byType = list.filter((x) => x.providerGroup === claim.providerGroup);
            if (list.length === 0) byType = base;
            if (!list.some((x) => x.caseType === claim.caseType) || claim.caseType == null) {
                byType = list.filter((x) => x.caseType == null);
            }
        }
        const key = 'descriptionText';
        const statusList = byType?.length === 0 && claim.returnPolicy === ' ' ? base : byType;
        const deduped = [...new Map(statusList?.map((item) => [item[key], item])).values()];
        const isDescriptionStep = deduped?.some((s) => s.descriptionText === claim.statusDescription);
        return { steps: deduped, isDescriptionStep };
    };

    const canApprove = (claim) => {
        const hasRole = roles?.includes('CanApproveRejectClaim');
        const enabledCode =
            Array.isArray(statusCodes) && statusCodes.find((o) => o.code === claim.statusCode)?.enabled === true;
        return hasRole && claim.returnPolicy !== 'F' && enabledCode;
    };

    const disabledApprovalReason = (claim) => {
        const arr = Array.isArray(statusCodes) ? statusCodes : [];
        const sc = arr.find((o) => String(o.code) === String(claim.statusCode));
        return sc?.status ?? localized.Pending;
    };

    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedRowId, setSelectedRowId] = React.useState(null);
    const openDrawer = (row) => {
        setSelectedRowId(row?.id ?? null);
        setDrawerOpen(true);
    };
    const closeDrawer = () => setDrawerOpen(false);

    const [modalOpen, setModalOpen] = React.useState(false);
    const [modalRow, setModalRow] = React.useState(null);
    const [modalSteps, setModalSteps] = React.useState([]);
    const [modalIsDescriptionStep, setModalIsDescriptionStep] = React.useState(false);

    const openModal = (row) => {
        const { steps, isDescriptionStep } = getStatusSteps(row);
        setModalRow(row);
        setModalSteps(steps ?? []);
        setModalIsDescriptionStep(!!isDescriptionStep);
        setModalOpen(true);
    };
    const closeModal = () => setModalOpen(false);

    const [showDetailModal, setShowDetailModal] = React.useState(false);
    const [detailMeta, setDetailMeta] = React.useState({
        itemNumber: '',
        serialNumber: '',
        detailComment: '',
    });
    const openDetailsModal = (row) => {
        setDetailMeta({
            itemNumber: row.itemNumber,
            serialNumber: row.serialNumber,
            detailComment: row.detailsComments,
        });
        setShowDetailModal(true);
    };
    const hideDetailsModal = () => setShowDetailModal(false);

    const customToolbar = () => {
        return (
            <GridToolbarContainer>
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{ tooltip: { title: 'Filter list' } }}
                    variant="outlined"
                    color="update"
                />
                <GridToolbarDensitySelector
                    slotProps={{ tooltip: { title: 'Change density' } }}
                    variant="outlined"
                    color="update"
                />
                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    csvOptions={{ allColumns: true }}
                    excelOptions={{ allColumns: true }}
                    slotProps={{ tooltip: { title: 'Export data' } }}
                    variant="outlined"
                    color="update"
                />
            </GridToolbarContainer>
        );
    };

    const listViewCell = (params) => {
        const row = params.row;
        const statusText = row.statusDescription ?? localized.Status;
        const canRma = validRMAStatusCode(row.statusCode) && row.returnPolicy !== 'F';
        return (
            <Box sx={{ width: '100%', pt: 1, pb: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography
                        variant="body1"
                        fontWeight={700}
                   >
                        {row.caseNumber}
                        {row.lineNumber ? `-${row.lineNumber}` : ''}
                    </Typography>
                    <Chip size="small" label={statusText} />
                </Stack>

                <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {row.orderId ? (
                        <Typography
                            variant="body2"
                            component={Link}
                            to={`/order/${row.orderId}/${row.orderCompany}/${row.orderType}`}
                            style={{ textDecoration: 'none' }}
                        >
                            <strong>{localized.RelatedOrder}:</strong> {row.orderId}
                        </Typography>
                    ) : null}
                    {row.itemNumber && (
                        <Typography variant="body2">
                            <strong>{localized.ItemNumber}:</strong> {row.itemNumber}
                        </Typography>
                    )}
                    {row.serialNumber && (
                        <Typography variant="body2">
                            <strong>{localized.SerialNumber}:</strong> {row.serialNumber}
                        </Typography>
                    )}
                    {row.referenceNumber && (
                        <Typography variant="body2">
                            <strong>{localized.ReferenceNumber}:</strong> {row.referenceNumber}
                        </Typography>
                    )}
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} alignItems="center">          
                    <Tooltip title={localized.ViewDetails}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation?.();
                                openDrawer(row);
                            }}
                        >
                            <ExpandMoreIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {row.detailsComments ? (
                        <Tooltip title={localized.DetailedComments}>
                            <IconButton size="small" onClick={() => openDetailsModal(row)}>
                                <DescriptionIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null}

                    {canRma && (
                        <Tooltip title={localized.RMA}>
                            <span>
                                <IconButton size="small" onClick={() => getRMA(row)}>
                                    <OrderAckIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Stack>
            </Box>
        );
    };

    const listViewColDef = {
        field: 'listColumn',
        headerName: localized.ClaimNumber,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: listViewCell,
        disableExport: true,
    };

    const columns = [
        {
            field: 'caseLine',
            headerName: localized.ClaimNumber,
            sortable: false,
            renderCell: (p) => (
                <div
                    style={{ cursor: 'pointer', textDecoration: 'underline', color: "blue" }}
                    onClick={() => openModal(p.row)}
                >
                    {p.row.caseNumber}
                    {p.row.lineNumber ? `-${p.row.lineNumber}` : ''}
                </div>
            ),
        },
        {
            field: 'orderId',
            headerName: localized.RelatedOrder,
            renderCell: (params) => {
                const r = params.row;
                if (!r.orderId) return null;
                return (
                    <Link to={`/order/${r.orderId}/${r.orderCompany}/${r.orderType}`}>{r.orderId}</Link>
                );
            },
        },
        { field: 'itemNumber', headerName: localized.ItemNumber, flex:1},
        { field: 'serialNumber', headerName: localized.SerialNumber, flex:1 },
        {
            field: 'dateEntered',
            headerName: localized.ClaimDate,
            renderCell: (p) => <DateDisplay date={p.row.dateEntered} />,
        },
        { field: 'statusDescription', headerName: localized.Status, flex:1 },
        { field: 'referenceNumber', headerName: localized.ReferenceNumber },
        {
            field: 'fastTrack',
            headerName: localized.FastTrack,
            align: 'center',
            renderCell: (p) =>
                p.row.returnPolicy && String(p.row.returnPolicy).trim() === 'F' ?  
                    (<div style={{ paddingLeft: "25px", paddingTop: "17px" }}> <CheckBox checked disabled /> </div>) : null,
        },
        {
            field: 'approval',
            flex: .5,
            align: 'center',
            headerName: localized.Action,
            filterable: false,
            sortable: false,
            renderCell: (p) => {
                const r = p.row;
                const showSelect = canApprove(r);
                if (showSelect) {
                    return (
                        <Select
                            size="small"
                            value={r.approvalStatus ?? ''}
                            onChange={(e) => setClaimApproval?.(r.caseNumber, e)}
                            displayEmpty
                            sx={{ minWidth: 100 }}
                        >
                            <MenuItem value="">{localized.Pending}</MenuItem>
                            <MenuItem value="1">{localized.Approve}</MenuItem>
                            <MenuItem value="0">{localized.Reject}</MenuItem>
                        </Select>
                    );
                }
                const txt = disabledApprovalReason(r);
                return <div style={{ justifySelf: "center", paddingTop: "17px" }}><Typography variant="body2">{txt}</Typography></div>;
            },
        },
        {
            field: 'rma',
            headerName: 'RMA',
            align: 'center',
            sortable: false,
            renderCell: (p) => {
                const r = p.row;
                const canRma = r.returnPolicy !== 'F' && validRMAStatusCode(r.statusCode);
                if (!canRma) return null;
                return (
                    <Tooltip title={localized.RMA}>
                        <IconButton size="small" onClick={() => getRMA(r)}>
                            <OrderAckIcon />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: 'detailedComments',
            headerName: localized.DetailedComments,
            align: 'center',
            sortable: false,
            renderCell: (p) =>
                p.row.detailsComments ? (
                    <Tooltip title={localized.DetailedComments}>
                        <IconButton size="small" onClick={() => openDetailsModal(p.row)}>
                            <DescriptionIcon fontSize="small" sx={{ color: '#555555 !important' }} />
                        </IconButton>
                    </Tooltip>
                ) : null,
        },
    ];

    const DetailsDrawer = () => {

        const row = React.useMemo(() => rows.find(r => r.id === selectedRowId) ?? null,
                [rows, selectedRowId]
        );

        if (!row) return null;
        const showRma = row.returnPolicy !== 'F' && validRMAStatusCode(row.statusCode);
        const showRelatedOrder = row.orderId && row.orderId > 0 ? true : false;
        const hasComments = row.comments && String(row.comments).trim().length > 0;
        const hasDetailComments = row.detailsComments && String(row.detailsComments).trim().length > 0;

        return (
            <SwipeableDrawer
                anchor="bottom"
                open={drawerOpen}
                onOpen={() => setDrawerOpen(true)}
                onClose={closeDrawer}
                PaperProps={{ sx: { borderTopLeftRadius: 12, borderTopRightRadius: 12, pb: 2 } }}
                transitionDuration={280}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                        <Typography variant="h6" fontWeight={700}>
                            {(localized.ClaimNumber)}: {row.caseNumber}
                            {row.lineNumber ? `-${row.lineNumber}` : ''}
                        </Typography>
                        <Chip size="small" label={row.statusDescription ?? ''} />
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={1.25}>
                        <Stack direction="row" spacing={4} flexWrap="wrap">
                            {row.itemNumber && (
                                <Box>
                                    <Typography variant="subtitle2"><strong>{localized.ItemNumber}</strong></Typography>
                                    <Typography variant="body2">{row.itemNumber}</Typography>
                                </Box>
                            )}
                            {row.serialNumber && (
                                <Box>
                                    <Typography variant="subtitle2"><strong>{localized.SerialNumber}</strong></Typography>
                                    <Typography variant="body2">{row.serialNumber}</Typography>
                                </Box>
                            )}
                            {row.referenceNumber && (
                                <Box>
                                    <Typography variant="subtitle2"><strong>{localized.ReferenceNumber}</strong></Typography>
                                    <Typography variant="body2">{row.referenceNumber}</Typography>
                                </Box>
                            )}
                        </Stack>

                        <Stack direction="row" spacing={4} flexWrap="wrap">
                            <Box>
                                <Typography variant="subtitle2"><strong>{localized.ClaimDate}</strong></Typography>
                                <DateDisplay date={row.dateEntered} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2"><strong>{localized.DateInstalled}</strong></Typography>
                                <DateDisplay date={row.installDate} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2"><strong>{localized.DateFailed}</strong></Typography>
                                <DateDisplay date={row.failureDate} />
                            </Box>                            
                        </Stack>
                        <Stack direction="row" spacing={4} flexWrap="wrap">
                            {showRelatedOrder && (
                                <Box>

                                    <Typography variant="subtitle2"><strong>{localized.RelatedOrder}</strong></Typography>
                                    <Typography variant="body2">
                                        <Link to={`/order/${row.orderId}/${row.orderCompany}/${row.orderType}`}>
                                            {row.orderId}
                                        </Link>
                                    </Typography>

                                </Box>
                            )}
                            <Box>
                                <Typography variant="subtitle2"><strong>{localized.LoyaltyID}</strong></Typography>
                                <Typography variant="body2">{row.loyaltyID}</Typography>
                            </Box>
                            {hasComments && (
                                <Box>
                                    <Typography variant="subtitle2"><strong>{localized.Comments}</strong></Typography>
                                    <Typography variant="body2">{row.comments}</Typography>
                                
                                </Box>
                            )}
                        </Stack>

                        <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 2 }}>
                            {row.returnPolicy && String(row.returnPolicy).trim() === 'F' && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    
                                <div className="d-flex justify-content-end align-items-center">
                                    <CheckBox checked disabled /> {localized.FastTrack}
                                </div>

                                    <Typography variant="body2"><strong>{localized.FastTrack}</strong></Typography>
                                </Stack>
                            )}                            
                            {showRma && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<OrderAckIcon />}
                                    onClick={() => getRMA(row)}
                                >
                                    {localized.RMA}
                                </Button>
                            )}
                            {hasDetailComments && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        paddingBottom: "8px",
                                        paddingTop: "7px"} }
                                    startIcon={<DescriptionIcon />}
  
                                    onClick={() => openDetailsModal(row)}
                                >
                                    {localized.DetailedComments}
                                </Button>
                            )}                            
                        </Stack>
                        {canApprove(row) ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                <Typography variant="subtitle2">{localized.Action}:</Typography>
                                <Select
                                    size="small"
                                    value={row.approvalStatus ?? ''}
                                    onChange={(e) => setClaimApproval?.(row.caseNumber, e)}
                                    displayEmpty
                                    sx={{ minWidth: 140 }}
                                >
                                    <MenuItem value="">{localized.Pending}</MenuItem>
                                    <MenuItem value="1">{localized.Approve}</MenuItem>
                                    <MenuItem value="0">{localized.Reject}</MenuItem>
                                </Select>
                            </Stack>
                        ) : null}                        
                    </Stack>
                </Box>
            </SwipeableDrawer>
        );
    };

    const DesktopModal = () => {
        const row = modalRow;
        if (!row) return null;

        return (
            <MUIModal
                open={modalOpen}
                onClose={closeModal}
                closeLabel={localized.Cancel}
                title={null}
                maxWidth="md"
                showDefaultButtons={false}
                cancelLabel={null}
                submitLabel={null}
            >
                <Container className="warranty-status-modal">
                    <Row className="heading1 gy-2 gx-4 align-items-center">
                        <Col xs={12} md="auto" className="text-start">
                            <div>
                                {localized.ClaimNumber} {row.caseNumber}
                                {row.lineNumber ? `-${row.lineNumber}` : ''}
                            </div>
                        </Col>
                        <Col xs={12} md className="text-md-end text-start">
                            {row.returnPolicy && row.returnPolicy.trim() === 'F' && (
                                <div className="d-inline-flex align-items-center gap-2">
                                    <CheckBox checked disabled /> {localized.FastTrack}
                                </div>
                            )}
                        </Col>
                    </Row>

                    <div className="my-3" />
                    <Row className="gy-3 gx-4">
                        <Col xs="auto" className="d-flex align-items-start gap-2 text-nowrap me-4">
                            <div className="heading4">{localized.Comments}:</div>
                        </Col>
                        <Col>
                            <div>{row.comments}</div>
                        </Col>
                    </Row>
                    <Row className="gy-3 gx-4 mt-2">
                        <Col xs="auto" className="d-flex align-items-center gap-2 text-nowrap me-4">
                            <div className="heading4">{localized.EnteredBy}:</div>
                        </Col>
                        <Col>
                            <div>{row.enteredByName}</div>
                        </Col>
                    </Row>
                    <Row className="gy-3 gx-5 align-items-center mt-2">
                        <Col xs="auto" className="d-inline-flex align-items-center gap-2 text-nowrap me-5">
                            <div className="heading4">{localized.DateInstalled}:</div>
                            <div>{row.installDate ? <DateDisplay date={row.installDate} /> : 'NA'}</div>
                        </Col>

                        <Col xs="auto" className="d-inline-flex align-items-center gap-2 text-nowrap me-5">
                            <div className="heading4">{localized.DateFailed}:</div>
                            <div>{row.failureDate ? <DateDisplay date={row.failureDate} /> : 'NA'}</div>
                        </Col>

                        <Col xs="auto" className="d-inline-flex align-items-center gap-2 text-nowrap me-5">
                            <div className="heading4">{localized.SerialNumber}:</div>
                            <div>{row.serialNumber}</div>
                        </Col>

                        <Col xs="auto" className="d-inline-flex align-items-center gap-2 text-nowrap">
                            <div className="heading4">{localized.LoyaltyID}:</div>
                            <div>{row.loyaltyID}</div>
                        </Col>
                    </Row>
                    {Array.isArray(modalSteps) && modalSteps.length > 0 && (
                        <Row className="mt-4">
                            <Col>
                                <div className="d-flex justify-content-center">
                                    <StepProgressBar
                                        width={600}
                                        steps={modalSteps}
                                        isDescriptionStep={modalIsDescriptionStep}
                                        stepDescription={row.statusDescription}
                                        stepCode={row.statusCode}
                                    />
                                </div>
                            </Col>
                        </Row>
                    )}
                </Container>
            </MUIModal>
        );
    };

    return (
        <>
            {(gridLocalized && Object.keys(gridLocalized).length > 0) && (
                <>
                    <ThemeProvider theme={wrapperTheme}>
                        <Paper sx={{ height: 700, width: '100%' }}>
                            <TranslatableDataGridServerLoaded
                                rows={rows}
                                columns={columns}
                                count={count}
                                loading={fetching}
                                fetching={fetching}
                                labels={gridLocalized}
                                square
                                toolbar={customToolbar}
                                paginationModel={paginationModel}
                                setPaginationModel={setPaginationModel}
                                listView={listView}
                                listViewColumn={listViewColDef}
                                getRowId={(row) => row.id}
                                getRowHeight={listView ? () => 'auto' : undefined}
                                getEstimatedRowHeight={listView ? () => 160 : undefined}
                            />
                            {listView && <DetailsDrawer />}
                        </Paper>
                    </ThemeProvider>
                    <DesktopModal />
                    <CommentDetailsModal
                        show={showDetailModal}
                        onHide={hideDetailsModal}
                        callback={hideDetailsModal}
                        localized={localized}
                        itemNumber={detailMeta.itemNumber}
                        serialNumber={detailMeta.serialNumber}
                        detailComment={detailMeta.detailComment}
                        detailCommentUpdated={() => { }}
                        showFooter={false}
                        IsDetailOnly={true}
                    />
                </>
            )}
        </>
    );
};

Claims.propTypes = {
    claims: PropTypes.array,
    count: PropTypes.number,
    fetching: PropTypes.bool,
    paginationModel: PropTypes.shape({
        page: PropTypes.number,
        pageSize: PropTypes.number,
    }),
    setPaginationModel: PropTypes.func,
    listView: PropTypes.bool,
    localized: PropTypes.object,
    roles: PropTypes.arrayOf(PropTypes.string),
    statusCodes: PropTypes.array,
    claimsStatus: PropTypes.array, 
    setClaimApproval: PropTypes.func,
};

export default Claims;